// vuex store for submodule: School Quality Snapshot (Year-specific)

import * as d3 from 'd3-collection'

import { SQSAllDataApi, SQSAllDataPreviewApi } from '@/api/data/sqs'
import { d3NestToMapRecurse, valIsMissing, boroughForDbn, isCharterDbn, schoolTypeCodeForName } from '@/utils/data'

const ELEMENT_CODES = ['SA', 'RI', 'CT', 'SE', 'ES', 'SF', 'TR']
const ALL_TAB_CODES = ['INFO'].concat(ELEMENT_CODES)
const NO_SA_SCHOOL_TYPES = ['EC', 'D75', 'Pre-K']

// initial state
const state = {
  isEmbargoed: true,
  layoutMetadata: undefined,
  schoolInfo: undefined,
  schoolInfoPK: undefined,
  selectedSchool: {},
  selectedSchoolData: undefined
}

// getters
const getters = {
  isEmbargoed: state => state.isEmbargoed,
  layoutMetadata: state => state.layoutMetadata,
  schoolInfo: state => state.schoolInfo,
  schoolInfoPK: state => state.schoolInfoPK,
  isSchoolInfoLoaded: state => (state.schoolInfo !== undefined && state.schoolInfoPK !== undefined),
  isSchoolDataLoaded: state => state.selectedSchoolData !== undefined,
  selectedSchoolData: state => state.selectedSchoolData,

  selectedSchool: state => state.selectedSchool,
  selectedSchoolType: state => schoolTypeCodeForName(state.selectedSchool.school_type),
  selectedSchoolName: state => state.selectedSchool.school || '',
  selectedSchoolBorough: state => boroughForDbn(state.selectedSchool.dbn) || '',

  hasSelectedSchool: state => state.selectedSchool.dbn !== undefined && state.selectedSchool.report_type !== undefined,
  isSelectedSchoolCharter: state => isCharterDbn(state.selectedSchool.dbn),
  isSelectedSchoolPK: state => state.selectedSchool.report_type === 'PK',
  isSelectedSchoolDistrictPK: (state, getters) => getters.isSelectedSchoolPK && state.selectedSchool.school_type === 'District Pre-K',
  isSelectedSchoolNYCEECPK: (state, getters) => getters.isSelectedSchoolPK && state.selectedSchool.school_type === 'NYCEEC',

  layoutMetadataForSchoolType: (state) => (schoolType) => {
    return state.layoutMetadata ? state.layoutMetadata[schoolType] || {} : {}
  },

  schoolListAll: (state, getters) => {
    if (!getters.isSchoolInfoLoaded) return undefined
    const combinedList = state.schoolInfoPK.concat(state.schoolInfo)
    combinedList.sort(function (a, b) {
      if (a.dbn > b.dbn) {
        return 1
      }
      if (a.dbn < b.dbn) {
        return -1
      }
      return a.report_type === 'PK' ? 1 : -1
    })
    return combinedList
  },
  schoolListAuthorized: (state, getters, rootState, rootGetters) => {
    const schoolList = getters.schoolListAll
    const isSPAdmin = rootGetters['isSPAdmin']
    const isLoggedIn = rootGetters['isLoggedIn']
    const authDBNs = rootGetters['authorizedDBNs']
    if (!getters.isSchoolInfoLoaded) {
      return undefined
    }
    if (!state.isEmbargoed || isSPAdmin) {
      return schoolList
    }
    if (!isLoggedIn) {
      return undefined
    }
    return schoolList.filter(s => authDBNs.indexOf(s.dbn) > -1)
  },
  // Flag for if user is trying to access embargoed report without permissions
  isUnauthorizedForEmbargo: (state, getters, rootState, rootGetters) => {
    const isLoggedIn = rootGetters['isLoggedIn']
    if (!state.isEmbargoed || !getters.isSchoolInfoLoaded || !isLoggedIn) {
      // Not embargoed, or don't know what auth status is yet
      return false
    }
    return getters.schoolListAuthorized.length === 0
  },

  // Custom getters for School Data
  valueForMetric: (state, getters) => (metricName) => {
    const d = state.selectedSchoolData
    return getters.isSchoolDataLoaded ? d[metricName] : undefined
  },
  elementRatingForCode: (state, getters) => (elementCode) => {
    // May be string or numeric, since it may be 'N/A'
    const ec = elementCode.toLowerCase()
    return getters.valueForMetric(`rating_${ec}`)
  },
  selectedSchoolNameLong: (state, getters) => {
    return getters.valueForMetric('location_name_long')
  },
  selectedSchoolPKCode: (state, getters) => {
    return getters.valueForMetric('program_code')
  },
  selectedSchoolDisplayName: (state, getters) => {
    const selectedSchool = getters.selectedSchool
    const selectedSchoolName = getters.selectedSchoolName
    const isPK = getters.isSelectedSchoolPK
    const isDistrictPK = getters.isSelectedSchoolDistrictPK

    const schoolName = getters.selectedSchoolNameLong || selectedSchoolName
    const dbn = selectedSchool ? selectedSchool.dbn : undefined
    const code = isPK ? getters.selectedSchoolPKCode : dbn
    const codeStr = code ? ` (${code})` : ''
    const pkStr = isDistrictPK ? `—Pre-K Program` : ''

    return schoolName ? `${schoolName}${codeStr}${pkStr}` : ''
  },
  selectedSchoolDistrict: (state, getters) => {
    return getters.valueForMetric('district')
  },
  selectedSchoolQRYear: (state, getters) => {
    return getters.valueForMetric('quality_review_year')
  },
  hasQRReview: (state, getters) => {
    // qrdates_of_review is for PK
    return !valIsMissing(getters.valueForMetric('dates_of_review')) ||
       !valIsMissing(getters.valueForMetric('qrdates_of_review'))
  },

  isSuppressSE: (state, getters) => {
    return +getters.valueForMetric('suppress_se') === 1
  },
  isSuppressSA: (state, getters) => {
    return +getters.valueForMetric('suppress_sa') === 1
  },
  isHighOptOutSchool: (state, getters) => {
    return +getters.valueForMetric('high_opt_out_sch') === 1
  },
  noRatingTextForCode: (state, getters) => (elementCode, defaultText) => {
    if ((elementCode === 'SA' && getters.isSuppressSA) || (elementCode === 'SE' && getters.isSuppressSE)) {
      return 'Missing Data†'
    } else if (elementCode === 'SA' && getters.isHighOptOutSchool) {
      return 'No Rating†'
    }
    return defaultText || 'No Rating'
  },

  isNoSASchoolType: (state, getters) => {
    const selectedSchoolType = getters.selectedSchoolType
    return NO_SA_SCHOOL_TYPES.indexOf(selectedSchoolType) > -1
  },
  selectedSchoolElementCodes: (state, getters) => {
    return getters.isNoSASchoolType ? ELEMENT_CODES.filter(d => d !== 'SA') : ELEMENT_CODES
  },
  selectedSchoolTabCodes: (state, getters) => {
    return getters.isNoSASchoolType ? ALL_TAB_CODES.filter(d => d !== 'SA') : ALL_TAB_CODES
  }
}

// actions
const actions = {
  async getLayoutMetadata (context, url) {
    const data = await context.dispatch('getDsvData', url, {root: true})

    const tmpData = d3.nest()
      .key(d => d.school_type)
      .key(d => d.tab_code)
      .key(d => d.col_num)
      .key(d => d.group_name)
      .key(d => d.component_index)
      .entries(data)
    const processedData = d3NestToMapRecurse(tmpData, 2)

    context.commit('setLayoutMetadata', processedData)
  },
  async getSchoolInfo (context, url) {
    const data = await context.dispatch('getDsvData', url, {root: true})
    context.commit('setSchoolInfo', data)
  },
  async getSchoolInfoPK (context, url) {
    const data = await context.dispatch('getDsvData', url, {root: true})
    context.commit('setSchoolInfoPK', data)
  },
  /**
   * @param {Object} apiParams - params to be passed to the api abstraction
   */
  async updateSelectedSchoolData (context, apiParams) {
    context.commit('clearSelectedSchoolData')
    const apiClass = context.state.isEmbargoed ? SQSAllDataPreviewApi : SQSAllDataApi
    // eslint-disable-next-line
    const dataApi = new apiClass(apiParams)
    const data = await context.dispatch('getApiData', dataApi, {root: true})

    const accumulator = (a, d) => { a[d.varname] = d.value; return a }
    let schoolData = data.reduce(accumulator, {})

    context.commit('setSelectedSchoolData', schoolData)
  }
}

// mutations
const mutations = {
  setIsEmbargoed (state, flag) {
    state.isEmbargoed = flag
  },
  setLayoutMetadata (state, data) {
    state.layoutMetadata = data
  },
  setSchoolInfo (state, data) {
    state.schoolInfo = data
  },
  setSchoolInfoPK (state, data) {
    state.schoolInfoPK = data
  },
  setSelectedSchool (state, school) {
    state.selectedSchool = school
  },
  clearSelectedSchool (state) {
    state.selectedSchool = {}
  },
  setSelectedSchoolData (state, data) {
    state.selectedSchoolData = data
  },
  clearSelectedSchoolData (state) {
    state.selectedSchoolData = undefined
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}



// WEBPACK FOOTER //
// ./src/store/modules/sqs/2018/index.js